// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { getAuthToken } from '../utils/auth';
import './SubscriptionPlans.css';

// Plans are now fetched from API

var FEATURE_LIST = [
    'Ad-free Experience',
    'Blog Premium Access',
    'Podcast Premium Access',
    'App Store Access',
    'Typewriting Practice',
    'Pomodoro Timer',
    'Priority Support',
];

var FREE_FEATURES = {
    'Ad-free Experience': false,
    'Blog Premium Access': false,
    'Podcast Premium Access': false,
    'App Store Access': false,
    'Typewriting Practice': 'Limited',
    'Pomodoro Timer': false,
    'Priority Support': false,
};

function isSeoOnlyPlan(plan) {
    var id = (plan && plan.planId ? plan.planId : '').toLowerCase();
    var name = (plan && plan.name ? plan.name : '').toLowerCase();
    return id === 'seo' || id === 'seo-fort' || id === 'seo_fort' || name === 'seo fort';
}

function isAiPlan(plan) {
    var id = (plan && plan.planId ? plan.planId : '').toLowerCase();
    var name = (plan && plan.name ? plan.name : '').toLowerCase();
    return id === 'ai' || name === 'ai' || name === 'premium' || name.indexOf(' ai') !== -1 || name.indexOf('ai ') !== -1;
}

function normalizePlansForUi(rawPlans) {
    var cloned = rawPlans.map(function (plan) {
        var name = plan.name;
        if (name === 'AI' || name === 'ai') {
            name = 'Premium';
        }
        return {
            planId: plan.planId,
            name: name,
            description: plan.description,
            popular: plan.popular,
            order: plan.order,
            pricing: plan.pricing,
            features: Object.assign({}, plan.features || {}),
        };
    });

    var seoPlan = cloned.find(function (p) { return isSeoOnlyPlan(p); });
    var aiPlan = cloned.find(function (p) { return isAiPlan(p); });
    if (aiPlan) {
        var appStoreAccess = aiPlan.features['App Store Access'] === true;

        if (appStoreAccess && aiPlan.features['Pomodoro Timer'] !== true) {
            aiPlan.features['Pomodoro Timer'] = true;
        }
        if (appStoreAccess && aiPlan.features['Typewriting Practice'] === undefined) {
            aiPlan.features['Typewriting Practice'] = true;
        }
    }

    return cloned.filter(function (p) { return !isSeoOnlyPlan(p); });
}

function SubscriptionPlans() {
    var navigate = useNavigate();
    var location = useLocation();
    var [billing, setBilling] = useState('monthly');
    var [loadingPayment, setLoadingPayment] = useState(false);
    var [selectedPlan, setSelectedPlan] = useState(null);
    var [isSubscribed, setIsSubscribed] = useState(false);
    var [activePlan, setActivePlan] = useState(null);
    var [checkingStatus, setCheckingStatus] = useState(true);

    var [plans, setPlans] = useState({});
    var [planKeys, setPlanKeys] = useState([]);
    var [currency, setCurrency] = useState('USD');
    var [currencySymbol, setCurrencySymbol] = useState('$');
    var [plansLoading, setPlansLoading] = useState(true);

    var token = getAuthToken();

    useEffect(function () {
        if (!token) {
            setCheckingStatus(false);
        } else {
            fetch(API_ENDPOINTS.SUBSCRIPTION.STATUS, {
                headers: { Authorization: 'Bearer ' + token },
            })
                .then(function (r) { return r.text(); })
                .then(function (text) {
                    var trimmed = text ? text.trim() : '';
                    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return;
                    try {
                        var data = JSON.parse(trimmed);
                        if (data && data.active) {
                            setIsSubscribed(true);
                            if (data.plan) setActivePlan(data.plan);
                        }
                    } catch (e) { /* ignore */ }
                })
                .catch(function () { /* ignore */ })
                .finally(function () { setCheckingStatus(false); });
        }

        // Fetch plans
        axios.get(API_ENDPOINTS.SUBSCRIPTION.PLANS)
            .then(function(res) {
                if(res.data && res.data.success) {
                    var data = normalizePlansForUi(res.data.data || []);
                    var plansObj = {};
                    var keys = [];
                    data.forEach(function(p) {
                        plansObj[p.planId] = p;
                        keys.push(p.planId);
                    });
                    setPlans(plansObj);
                    setPlanKeys(keys);
                }
            })
            .catch(function(err) { console.error('Failed to fetch plans', err); })
            .finally(function() { setPlansLoading(false); });

        // Auto detect currency via IP
        fetch('https://ipapi.co/json/')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                var c = data.country_code;
                if (c === 'IN') {
                    setCurrency('INR');
                    setCurrencySymbol('₹');
                } else if (['AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES'].includes(c)) {
                    setCurrency('EUR');
                    setCurrencySymbol('€');
                } else {
                    setCurrency('USD');
                    setCurrencySymbol('$');
                }
            })
            .catch(function() {
                setCurrency('USD');
                setCurrencySymbol('$');
            });
    }, [token]);

    var handleSubscribe = function (planId) {
        if (!token) {
            navigate('/signin', { state: { from: location } });
            return;
        }

        setSelectedPlan(planId);
        setLoadingPayment(true);

        var loadRazorpay = function () {
            if (window.Razorpay) return Promise.resolve();
            return new Promise(function (resolve) {
                var script = document.createElement('script');
                script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                script.async = true;
                script.onload = resolve;
                document.body.appendChild(script);
            });
        };

        var plan = plans[planId];

        loadRazorpay()
            .then(function () {
                return axios.post(API_ENDPOINTS.SUBSCRIPTION.CREATE_ORDER, {
                    planId: planId,
                    billing: billing,
                    currency: currency,
                }, {
                    headers: { Authorization: 'Bearer ' + token },
                });
            })
            .then(function (res) {
                var data = res.data;
                if (!data.success) throw new Error('Order creation failed');

                var options = {
                    key: data.data.key,
                    amount: data.data.amount,
                    currency: data.data.currency,
                    name: 'Hari Vishnu',
                    description: plan.name + ' Plan (' + billing + ')',
                    order_id: data.data.razorpayOrderId,
                    handler: function (response) {
                        axios.post(API_ENDPOINTS.SUBSCRIPTION.VERIFY_PAYMENT, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            subscriptionId: data.data.subscriptionId,
                        }, {
                            headers: { Authorization: 'Bearer ' + token },
                        })
                            .then(function (verifyRes) {
                                if (verifyRes.data.success) {
                                    setIsSubscribed(true);
                                    setActivePlan(planId);
                                    localStorage.removeItem('lw_free_prompts_used');
                                }
                            })
                            .catch(function () {
                                alert('Payment verification failed. Please contact support.');
                            });
                    },
                    theme: { color: '#47b5ff' },
                };

                var rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    alert('Payment failed: ' + response.error.description);
                });
                rzp.open();
            })
            .catch(function (err) {
                console.error(err);
                alert('Failed to initiate payment. Please try again.');
            })
            .finally(function () {
                setLoadingPayment(false);
                setSelectedPlan(null);
            });
    };

    var renderFeatureCell = function (value) {
        if (value === true) {
            return <i className="fi fi-sr-check-circle sp-check"></i>;
        }
        if (value === false) {
            return <span className="sp-dash">&mdash;</span>;
        }
        return <span className="sp-cell-text">{value}</span>;
    };

    var schemaData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Subscription Plans - Hari Vishnu',
        description: 'Choose your plan. Ad-Free, Content, or Premium — pick what suits you best.',
        url: 'https://www.harivishnu.in/subscription',
    };

    if (plansLoading) {
        return <div className="sp-page min-h-[50vh] text-center" style={{textAlign: 'center', padding: '100px'}}>Loading plans...</div>;
    }

    return (
        <div className="sp-page mx-auto w-full max-w-7xl px-4 pb-10 pt-6">
            <Helmet>
                <title>Subscription Plans | Hari Vishnu</title>
                <meta name="description" content="Choose your plan. Ad-Free removes ads, Content adds premium blog & podcast, Premium unlocks everything." />
                <link rel="canonical" href="https://www.harivishnu.in/subscription" />
                <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
            </Helmet>

            {/* Hero */}
            <div className="sp-hero rounded-3xl px-5 py-8 md:px-8">
                <div className="sp-hero-badge">Plans</div>
                <h1 className="sp-hero-title">Find the right plan for you</h1>
                <p className="sp-hero-sub">
                    Start free or upgrade to remove ads, unlock premium content, and access all premium tools.
                </p>
            </div>

            {/* Billing Toggle */}
            <div className="sp-billing-toggle mt-6 flex flex-wrap items-center justify-center gap-2">
                <button
                    className={'sp-toggle-btn' + (billing === 'monthly' ? ' sp-toggle-active' : '')}
                    onClick={function () { setBilling('monthly'); }}
                >
                    Monthly
                </button>
                <button
                    className={'sp-toggle-btn' + (billing === 'yearly' ? ' sp-toggle-active' : '')}
                    onClick={function () { setBilling('yearly'); }}
                >
                    Annual
                    <span className="sp-save-badge">Save up to 17%</span>
                </button>
            </div>

            {/* Plan Cards */}
            <div className="sp-plans-section mt-8">
                <div className="sp-plans-grid">
                    {planKeys.map(function (key) {
                        var plan = plans[key];
                        var pricingEntry = plan.pricing.find(function(p) { return p.currency === currency; }) || plan.pricing.find(function(p) { return p.currency === 'USD'; }) || plan.pricing[0];
                        var price = billing === 'monthly' ? pricingEntry.monthly : pricingEntry.yearly;
                        var freq = billing === 'monthly' ? '/mo' : '/yr';
                        var isActive = isSubscribed && activePlan === key;

                        return (
                            <div key={key} className={'sp-plan-card' + (plan.popular ? ' sp-plan-popular' : '')}>
                                {plan.popular && <div className="sp-popular-tag">Most Popular</div>}
                                <div className="sp-plan-header">
                                    <h2 className="sp-plan-name">{plan.name}</h2>
                                    <div className="sp-plan-price">
                                        <span className="sp-price-currency">{currencySymbol}</span>
                                        <span className="sp-price-amount">{price}</span>
                                        <span className="sp-price-freq">{freq}</span>
                                    </div>
                                    <p className="sp-plan-desc">{plan.description}</p>
                                </div>
                                <ul className="sp-plan-features">
                                    {FEATURE_LIST.map(function (feat) {
                                        var val = plan.features[feat];
                                        if (val === false) return null;
                                        return (
                                            <li key={feat} className="sp-feature-item">
                                                <i className="fi fi-sr-check-circle sp-check"></i>
                                                <span>{feat}</span>
                                                {typeof val === 'string' && <span className="sp-feature-badge">{val}</span>}
                                            </li>
                                        );
                                    })}
                                </ul>
                                <div className="sp-plan-action">
                                    {isActive ? (
                                        <button className="sp-btn sp-btn-active" disabled>
                                            <i className="fi fi-sr-check"></i> Active
                                        </button>
                                    ) : (
                                        <button
                                            className={'sp-btn sp-btn-primary' + (loadingPayment && selectedPlan === key ? ' sp-btn-loading' : '')}
                                            onClick={function () { handleSubscribe(key); }}
                                            disabled={loadingPayment || checkingStatus}
                                        >
                                            {loadingPayment && selectedPlan === key ? 'Processing...' : 'Get ' + plan.name}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Comparison Table */}
            <div className="sp-compare-section mt-10 rounded-2xl bg-white/90 p-4 shadow-sm md:p-6">
                <h2 className="sp-compare-title">Compare plans</h2>
                <div className="sp-compare-table-wrap">
                    <table className="sp-compare-table">
                        <thead>
                            <tr>
                                <th className="sp-th-feature"></th>
                                <th className="sp-th-plan">Free</th>
                                {planKeys.map(function (key) {
                                    var plan = plans[key];
                                    return (
                                        <th key={key} className={'sp-th-plan' + (plan.popular ? ' sp-th-highlight' : '')}>
                                            {plan.name}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {FEATURE_LIST.map(function (feat, i) {
                                return (
                                    <tr key={feat} className={i % 2 === 0 ? 'sp-row-even' : ''}>
                                        <td className="sp-td-feature">{feat}</td>
                                        <td className="sp-td-value">{renderFeatureCell(FREE_FEATURES[feat])}</td>
                                        {planKeys.map(function (key) {
                                            var plan = plans[key];
                                            return (
                                                <td key={key} className={'sp-td-value' + (plan.popular ? ' sp-td-highlight' : '')}>
                                                    {renderFeatureCell(plan.features[feat])}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Trust Section */}
            <div className="sp-trust-section mt-10">
                <div className="sp-trust-grid">
                    <div className="sp-trust-item">
                        <i className="fi fi-sr-shield-check"></i>
                        <h3>Secured by Razorpay</h3>
                        <p>All payments are processed securely through Razorpay, India's trusted payment gateway.</p>
                    </div>
                    <div className="sp-trust-item">
                        <i className="fi fi-sr-refresh"></i>
                        <h3>Cancel Anytime</h3>
                        <p>No lock-in. Cancel your subscription at any time from your profile settings.</p>
                    </div>
                    <div className="sp-trust-item">
                        <i className="fi fi-sr-clip-mail"></i>
                        <h3>Support Included</h3>
                        <p>Premium subscribers get priority support for any issues or questions at support@harivishnu.in.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SubscriptionPlans;
